import { useQuery } from '@tanstack/react-query'
import type { Invitation, WorkerLogin } from '../types.js'
import { operatorWorkerLoginApi, workerLoginQueryKey } from './workerLoginApi.js'

export type WorkerLoginState = 'none' | 'invited' | 'expired' | 'accepted' | 'password'

/** Where a worker's sign-in to the app stands, in words a facility's staff read at a glance. */
export interface WorkerLoginStatus {
  state: WorkerLoginState
  /** The chip: No login, Invited · waiting, Invitation expired, Accepted, Created with a password. */
  label: string
  /** One line saying when and by whom, or what to do next. */
  detail: string
}

function day(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function moment(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function sentLine(invitation: Invitation): string {
  const by = invitation.invitedBy.name ? ` by ${invitation.invitedBy.name}` : ''
  const texted = invitation.sends.some((send) => send.channel === 'sms' && send.status === 'sent') ? ', emailed and texted' : ''
  return `Sent ${day(invitation.createdAt)}${by}${texted}`
}

/**
 * The login's state from the login record and, while it waits, its open
 * invitation: nothing yet; invited and waiting (or past its seven days);
 * accepted, with when where the API kept it (since 29 October 2026); or a
 * login made with a password, which never had an invitation.
 */
export function workerLoginStatus(login: WorkerLogin | null, invitation: Invitation | null): WorkerLoginStatus {
  if (login === null) {
    return { state: 'none', label: 'No login', detail: 'Not invited to the app yet. Invite them from Edit worker.' }
  }
  if (login.invitationStatus === 'pending') {
    if (invitation?.status === 'expired') {
      return { state: 'expired', label: 'Invitation expired', detail: `${sentLine(invitation)}; it lapsed ${day(invitation.expiresAt)}. Resend it from Edit worker.` }
    }
    return { state: 'invited', label: 'Invited · waiting', detail: invitation ? `${sentLine(invitation)} · expires ${day(invitation.expiresAt)}` : 'Waiting for the worker to set their password.' }
  }
  if (login.invitationStatus === 'accepted') {
    return { state: 'accepted', label: 'Accepted', detail: login.invitationAcceptedAt ? `Accepted the invitation ${moment(login.invitationAcceptedAt)}` : 'Accepted the invitation; the time was not recorded then.' }
  }
  return { state: 'password', label: 'Created with a password', detail: `Login made ${moment(login.createdAt)}, without an invitation` }
}

/**
 * The worker's login and, while it waits, its invitation, through the same
 * queries as the Edit worker dialog's login section, so a change made there
 * shows here at once.
 */
export function useWorkerLoginStatus(clientId: string | undefined, facilityId: string, workerId: string) {
  const enabled = Boolean(clientId)
  const api = operatorWorkerLoginApi(clientId ?? '', facilityId, workerId)
  const queryKey = workerLoginQueryKey(clientId ?? '', workerId)
  const loginQuery = useQuery({ queryKey, queryFn: api.get, enabled })
  const pending = loginQuery.data?.invitationStatus === 'pending'
  const invitationQuery = useQuery({ queryKey: [...queryKey, 'invitation'], queryFn: api.invitation, enabled: enabled && pending })
  const isPending = !enabled || loginQuery.isPending || (pending && invitationQuery.isPending)
  return {
    login: loginQuery.data ?? null,
    status: loginQuery.data === undefined ? null : workerLoginStatus(loginQuery.data, invitationQuery.data?.invitation ?? null),
    isPending,
    isError: loginQuery.isError,
    refetch: () => void loginQuery.refetch(),
    isFetching: loginQuery.isFetching,
  }
}
